// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Admin API module for brokerage administration operations
 */

import api from './advisorApi';
import type {
  BrokerageDashboard,
  AdvisorMetrics,
  AdvisorRelationshipCount,
  AdvisorInviteRequest,
  AdvisorRoleChangeRequest,
  AdvisorDeactivateRequest,
  RelationshipReassignRequest,
  RelationshipBulkReassignRequest,
  RelationshipBulkReassignResult,
  RelationshipAssignmentHistory,
  AdvisorWithAdmin,
  AdvisorListFilter,
  PageResponse,
} from '../types/admin';
import type { Relationship } from '../types/relationship';

/**
 * Admin API endpoints for brokerage administration
 */
export const adminApi = {
  // ========== Dashboard Endpoints ==========

  /**
   * Get brokerage dashboard summary with alerts
   */
  getDashboardSummary: async (): Promise<BrokerageDashboard> => {
    const response = await api.get('/dashboard/brokerage');
    return response.data;
  },

  /**
   * Get metrics for all advisors in the brokerage
   */
  getAdvisorMetrics: async (
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<AdvisorMetrics>> => {
    const response = await api.get('/dashboard/advisors/metrics', {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Get relationship distribution across advisors
   */
  getRelationshipDistribution: async (): Promise<AdvisorRelationshipCount[]> => {
    const response = await api.get('/dashboard/profiles/distribution'); // Backend endpoint uses 'profiles'
    return response.data;
  },

  // ========== Advisor Management Endpoints ==========

  /**
   * Invite a new advisor to the brokerage
   */
  inviteAdvisor: async (request: AdvisorInviteRequest): Promise<AdvisorWithAdmin> => {
    const response = await api.post('/advisors', request);
    return response.data;
  },

  /**
   * List all advisors with filters and pagination
   */
  listAdvisors: async (
    filter: AdvisorListFilter = {},
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<AdvisorWithAdmin>> => {
    const response = await api.get('/advisors', {
      params: {
        search: filter.search || undefined,
        status: filter.status === 'all' ? undefined : filter.status,
        role: filter.role === 'all' ? undefined : filter.role,
        page,
        size,
      },
    });
    return response.data;
  },

  /**
   * Get a single advisor by ID
   */
  getAdvisor: async (advisorId: string): Promise<AdvisorWithAdmin> => {
    const response = await api.get(`/advisors/${advisorId}`);
    return response.data;
  },

  /**
   * Grant or revoke admin role for an advisor
   */
  setAdvisorAdminRole: async (
    advisorId: string,
    request: AdvisorRoleChangeRequest
  ): Promise<AdvisorWithAdmin> => {
    const response = await api.put(`/advisors/${advisorId}/admin`, request);
    return response.data;
  },

  /**
   * Deactivate an advisor with optional relationship reassignment
   */
  deactivateAdvisor: async (
    advisorId: string,
    request: AdvisorDeactivateRequest
  ): Promise<AdvisorWithAdmin> => {
    const response = await api.post(`/advisors/${advisorId}/deactivate`, request);
    return response.data;
  },

  /**
   * Reactivate a previously deactivated advisor
   */
  reactivateAdvisor: async (advisorId: string): Promise<AdvisorWithAdmin> => {
    const response = await api.post(`/advisors/${advisorId}/reactivate`);
    return response.data;
  },

  // ========== Relationship Reassignment Endpoints ==========

  /**
   * Reassign a single relationship to a different advisor
   */
  reassignRelationship: async (
    relationshipId: string,
    request: RelationshipReassignRequest
  ): Promise<Relationship> => {
    const response = await api.post(`/profiles/${relationshipId}/reassign`, request); // Backend endpoint uses 'profiles'
    return response.data;
  },

  /**
   * Bulk reassign multiple relationships to a single advisor
   */
  bulkReassignRelationships: async (
    request: RelationshipBulkReassignRequest
  ): Promise<RelationshipBulkReassignResult> => {
    const response = await api.post('/profiles/bulk-reassign', request); // Backend endpoint uses 'profiles'
    return response.data;
  },

  /**
   * Get relationship assignment history with pagination
   */
  getAssignmentHistory: async (
    page: number = 0,
    size: number = 20,
    relationshipId?: string,
    advisorId?: string
  ): Promise<PageResponse<RelationshipAssignmentHistory>> => {
    const response = await api.get('/profiles/assignment-history', { // Backend endpoint uses 'profiles'
      params: {
        page,
        size,
        profileId: relationshipId || undefined, // Backend param name is 'profileId'
        advisorId: advisorId || undefined,
      },
    });
    return response.data;
  },

  /**
   * Get assignment history for a specific relationship
   */
  getRelationshipAssignmentHistory: async (
    relationshipId: string
  ): Promise<RelationshipAssignmentHistory[]> => {
    const response = await api.get(`/profiles/${relationshipId}/assignment-history`); // Backend endpoint uses 'profiles'
    return response.data;
  },

  // ========== Admin Data Access Endpoints ==========

  /**
   * Get all clients across the brokerage (admin only)
   */
  getAllClients: async (
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<unknown>> => {
    const response = await api.get('/clients', {
      params: { page, size, all: true },
    });
    return response.data;
  },

  /**
   * Get all policies across the brokerage (admin only)
   */
  getAllPolicies: async (
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<unknown>> => {
    const response = await api.get('/policies', {
      params: { page, size, all: true },
    });
    return response.data;
  },

  /**
   * Get all LOAs across the brokerage (admin only)
   */
  getAllLOAs: async (
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<unknown>> => {
    const response = await api.get('/loa', {
      params: { page, size, all: true },
    });
    return response.data;
  },

  // ========== Admin Role Check ==========

  /**
   * Check if current user has admin role
   */
  checkAdminRole: async (): Promise<boolean> => {
    try {
      // Dashboard endpoint requires admin role, so successful call means admin
      await api.get('/dashboard/brokerage');
      return true;
    } catch {
      return false;
    }
  },
};

export default adminApi;
